<script setup>
import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import AgentField from './AgentField.vue';
import AgentSection from './AgentSection.vue';
import AgentToggle from './AgentToggle.vue';
import { createSectionFormProxy } from './createSectionFormProxy';

const props = defineProps({
  form: { type: Object, required: true },
  memoryForm: { type: Object, required: true },
  sourceCards: { type: Array, default: () => [] },
  improvementItems: { type: Array, default: () => [] },
  faqLearningManagement: {
    type: Object,
    default: () => ({
      availableSourceGroups: [],
      questionsGroup: {},
      enabled: true,
      locked: false,
    }),
  },
  selectedFaqGroupIds: { type: [Array, Set], default: () => [] },
  canManage: { type: Boolean, default: false },
  hasAdvancedManagement: { type: Boolean, default: false },
  isSavingKnowledge: { type: Boolean, default: false },
  isSavingFaqLearning: { type: Boolean, default: false },
  isSavingMemory: { type: Boolean, default: false },
  editingMemoryId: { type: [Number, String], default: null },
  memories: { type: Array, default: () => [] },
  memoriesTotalCount: { type: Number, default: 0 },
  hasMoreMemories: { type: Boolean, default: false },
  isLoadingMoreMemories: { type: Boolean, default: false },
});

const emit = defineEmits([
  'updateField',
  'updateMemoryField',
  'toggleFaqGroup',
  'saveKnowledge',
  'saveFaqLearning',
  'saveMemory',
  'cancelMemoryEdit',
  'editMemory',
  'deleteMemory',
  'loadMoreMemories',
  'openRoute',
]);

const { t: ce, te } = useI18n();
const formModel = createSectionFormProxy(
  () => props.form,
  payload => emit('updateField', payload)
);
const memoryFormModel = createSectionFormProxy(
  () => props.memoryForm,
  payload => emit('updateMemoryField', payload)
);

const isFaqGroupSelected = group => {
  const normalizedJid = group.jid.toLowerCase();
  return props.selectedFaqGroupIds instanceof Set
    ? props.selectedFaqGroupIds.has(normalizedJid)
    : props.selectedFaqGroupIds.includes(normalizedJid);
};

const memoryTypeLabel = memory => {
  const key = `CAPTAIN_EXPERIENCE.MEMORY_TYPE.${String(
    memory.memory_type || ''
  ).toUpperCase()}`;
  return te(key) ? ce(key) : memory.type_label || memory.memory_type;
};

const memoryContentInput = ref(null);

watch(
  () => props.editingMemoryId,
  async editingMemoryId => {
    if (!editingMemoryId) return;

    await nextTick();
    memoryContentInput.value?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
    });
    memoryContentInput.value?.focus?.();
  }
);
</script>

<template>
  <section id="agent-knowledge" class="flex flex-col gap-8" tabindex="-1">
    <div class="flex min-w-0 flex-col gap-5">
      <AgentSection
        :title="ce('CAPTAIN_EXPERIENCE.AUTO.FONTES_DE_CONHECIMENTO')"
      >
        <div class="flex flex-col gap-3">
          <CardLayout
            v-for="source in sourceCards"
            :id="source.focusId"
            :key="source.title"
            :tabindex="source.focusId ? -1 : undefined"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <h3 class="text-base font-medium text-n-slate-12">
                  {{ source.title }}
                </h3>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ source.description }}
                </p>
              </div>
              <span
                class="shrink-0 rounded-full px-2 py-1 text-xs font-medium"
                :class="
                  source.ready === null
                    ? 'bg-n-alpha-2 text-n-slate-11'
                    : source.ready
                      ? 'bg-n-teal-9/10 text-n-teal-11'
                      : 'bg-n-amber-9/10 text-n-amber-11'
                "
              >
                {{ source.status }}
              </span>
            </div>
            <Button
              v-if="source.routeName"
              type="button"
              color="slate"
              variant="ghost"
              size="sm"
              class="self-start"
              icon="i-lucide-arrow-up-right"
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.ABRIR')"
              @click="emit('openRoute', source.routeName)"
            />
          </CardLayout>
        </div>
      </AgentSection>

      <AgentSection
        :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.KNOWLEDGE_USAGE_TITLE')"
      >
        <form
          class="flex flex-col gap-3"
          @submit.prevent="emit('saveKnowledge')"
        >
          <fieldset
            :disabled="!canManage"
            class="contents disabled:pointer-events-none disabled:opacity-70"
          >
            <p class="text-sm leading-relaxed text-n-slate-11">
              {{
                ce('CAPTAIN_EXPERIENCE.WORKSPACE.KNOWLEDGE_USAGE_DESCRIPTION')
              }}
            </p>
            <AgentToggle
              v-model="formModel.featureFaq"
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.USAR_RESPOSTAS_APROVADAS')"
              :helper="
                ce('CAPTAIN_EXPERIENCE.WORKSPACE.APPROVED_RESPONSES_HELPER')
              "
            />
            <AgentToggle
              v-if="hasAdvancedManagement"
              v-model="formModel.featureMemory"
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.USAR_LEMBRANCAS')"
              :helper="ce('CAPTAIN_EXPERIENCE.WORKSPACE.MEMORIES_HELPER')"
            />
            <Button
              v-if="canManage"
              type="submit"
              icon="i-lucide-save"
              class="mt-1 w-fit"
              :is-loading="isSavingKnowledge"
            >
              {{ ce('CAPTAIN_EXPERIENCE.WORKSPACE.SAVE_KNOWLEDGE_SETTINGS') }}
            </Button>
          </fieldset>
        </form>
      </AgentSection>

      <AgentSection :title="ce('CAPTAIN_EXPERIENCE.AUTO.LACUNAS_PARA_REVISAR')">
        <div class="flex flex-col divide-y divide-n-weak">
          <article
            v-for="item in improvementItems.slice(0, 4)"
            :key="item.title"
            class="flex items-start gap-3 py-3 first:pt-0"
          >
            <span
              class="mt-0.5 size-4 shrink-0 text-n-slate-11"
              :class="item.icon"
              aria-hidden="true"
            />
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ item.title }}
              </p>
              <p class="mt-1 text-xs text-n-slate-11">{{ item.helper }}</p>
            </div>
          </article>
        </div>
      </AgentSection>
    </div>

    <AgentSection
      v-if="hasAdvancedManagement"
      :title="ce('CAPTAIN_EXPERIENCE.SCRIPT.FAQ_LEARNING_SECTION_TITLE')"
    >
      <form class="grid gap-4" @submit.prevent="emit('saveFaqLearning')">
        <div
          v-if="faqLearningManagement.locked || !faqLearningManagement.enabled"
          class="flex items-start gap-2 rounded-xl border border-n-weak bg-n-alpha-2 p-3 text-xs leading-relaxed text-n-slate-11"
        >
          <span class="i-lucide-lock-keyhole mt-0.5 size-4 shrink-0" />
          {{
            ce(
              faqLearningManagement.locked
                ? 'CAPTAIN_EXPERIENCE.WORKSPACE.FAQ_CONFIGURATION_LOCKED'
                : 'CAPTAIN_EXPERIENCE.WORKSPACE.FAQ_CONFIGURATION_DISABLED'
            )
          }}
        </div>
        <fieldset
          :disabled="
            !canManage ||
            faqLearningManagement.locked ||
            !faqLearningManagement.enabled
          "
          class="contents disabled:pointer-events-none disabled:opacity-70"
        >
          <p class="text-sm leading-relaxed text-n-slate-11">
            {{
              ce('CAPTAIN_EXPERIENCE.SCRIPT.FAQ_LEARNING_SECTION_DESCRIPTION')
            }}
          </p>
          <AgentField
            :label="
              ce('CAPTAIN_EXPERIENCE.SCRIPT.FAQ_LEARNING_SOURCE_GROUPS_LABEL')
            "
            :helper="
              ce('CAPTAIN_EXPERIENCE.SCRIPT.FAQ_LEARNING_SOURCE_GROUPS_HELPER')
            "
            :control-id="
              faqLearningManagement.availableSourceGroups.length
                ? ''
                : 'agent-faq-source-groups'
            "
            :grouped="
              Boolean(faqLearningManagement.availableSourceGroups.length)
            "
          >
            <div
              v-if="faqLearningManagement.availableSourceGroups.length"
              class="grid gap-2 sm:grid-cols-2"
            >
              <label
                v-for="group in faqLearningManagement.availableSourceGroups"
                :key="group.jid"
                class="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border border-n-weak bg-n-solid-2 px-3 py-2.5 text-sm text-n-slate-12 transition hover:border-n-brand"
              >
                <input
                  type="checkbox"
                  class="size-4 rounded border-n-weak text-n-blue-11"
                  :checked="isFaqGroupSelected(group)"
                  @change="
                    emit('toggleFaqGroup', {
                      group,
                      selected: $event.target.checked,
                    })
                  "
                />
                <span class="min-w-0 truncate font-medium">
                  {{ group.name || group.jid }}
                </span>
              </label>
            </div>
            <textarea
              v-else
              id="agent-faq-source-groups"
              v-model="formModel.faqLearningSourceGroupsText"
              class="agent-textarea text-xs"
              rows="6"
              :placeholder="
                ce(
                  'CAPTAIN_EXPERIENCE.SCRIPT.FAQ_LEARNING_SOURCE_GROUPS_PLACEHOLDER'
                )
              "
              aria-describedby="agent-faq-source-groups-description"
            />
          </AgentField>
          <AgentField
            :label="
              ce('CAPTAIN_EXPERIENCE.SCRIPT.FAQ_LEARNING_QUESTIONS_GROUP_LABEL')
            "
            :helper="
              ce(
                'CAPTAIN_EXPERIENCE.SCRIPT.FAQ_LEARNING_QUESTIONS_GROUP_HELPER'
              )
            "
            :control-id="
              faqLearningManagement.questionsGroup.jid
                ? ''
                : 'agent-faq-questions-group'
            "
            :grouped="Boolean(faqLearningManagement.questionsGroup.jid)"
          >
            <div
              v-if="faqLearningManagement.questionsGroup.jid"
              class="flex min-h-12 items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-2 px-3 py-2.5"
            >
              <span
                class="i-lucide-message-circle-question size-4 shrink-0 text-n-blue-11"
                aria-hidden="true"
              />
              <span class="min-w-0 text-sm font-medium text-n-slate-12">
                {{
                  faqLearningManagement.questionsGroup.name ||
                  ce('CAPTAIN_EXPERIENCE.WORKSPACE.FAQ_MANAGED_DESTINATION')
                }}
              </span>
            </div>
            <input
              v-else
              id="agent-faq-questions-group"
              v-model="formModel.faqLearningQuestionsGroupJid"
              class="agent-input text-xs"
              :placeholder="
                ce(
                  'CAPTAIN_EXPERIENCE.SCRIPT.FAQ_LEARNING_QUESTIONS_GROUP_PLACEHOLDER'
                )
              "
              aria-describedby="agent-faq-questions-group-description"
            />
          </AgentField>
          <Button
            v-if="canManage"
            type="submit"
            icon="i-lucide-save"
            class="w-fit"
            :is-loading="isSavingFaqLearning"
          >
            {{ ce('CAPTAIN_EXPERIENCE.SCRIPT.SAVE_FAQ_LEARNING') }}
          </Button>
        </fieldset>
      </form>
    </AgentSection>

    <AgentSection
      v-if="hasAdvancedManagement"
      :title="ce('CAPTAIN_EXPERIENCE.AUTO.ADICIONAR_LEMBRANCA')"
    >
      <form
        v-if="canManage"
        class="flex flex-col gap-3"
        @submit.prevent="emit('saveMemory')"
      >
        <AgentField
          :label="ce('CAPTAIN_EXPERIENCE.AUTO.TIPO_DE_LEMBRANCA')"
          control-id="agent-memory-type"
        >
          <select
            id="agent-memory-type"
            v-model="memoryFormModel.memoryType"
            class="agent-input"
          >
            <option value="fact">
              {{ ce('CAPTAIN_EXPERIENCE.AUTO.FATO') }}
            </option>
            <option value="preference">
              {{ ce('CAPTAIN_EXPERIENCE.AUTO.PREFERENCIA') }}
            </option>
            <option value="correction">
              {{ ce('CAPTAIN_EXPERIENCE.AUTO.CORRECAO') }}
            </option>
            <option value="procedure">
              {{ ce('CAPTAIN_EXPERIENCE.AUTO.PROCEDIMENTO') }}
            </option>
          </select>
        </AgentField>
        <AgentField
          :label="ce('CAPTAIN_EXPERIENCE.AUTO.ASSUNTO')"
          control-id="agent-memory-key"
        >
          <input
            id="agent-memory-key"
            v-model="memoryFormModel.key"
            class="agent-input"
            :placeholder="
              ce('CAPTAIN_EXPERIENCE.AUTO.EX_PREFERENCIA_DE_REGIAO')
            "
          />
        </AgentField>
        <AgentField
          :label="ce('CAPTAIN_EXPERIENCE.AUTO.O_QUE_O_AGENTE_DEVE_LEMBRAR')"
          control-id="agent-memory-content"
          required
        >
          <textarea
            id="agent-memory-content"
            ref="memoryContentInput"
            v-model="memoryFormModel.content"
            class="agent-textarea"
            rows="4"
            required
            :placeholder="
              ce(
                'CAPTAIN_EXPERIENCE.AUTO.EX_CLIENTES_DESSE_PERFIL_PREFEREM_IMOVEIS_PROXIMOS_AO_ME'
              )
            "
          />
        </AgentField>
        <Button
          type="submit"
          :icon="editingMemoryId ? 'i-lucide-save' : 'i-lucide-plus'"
          :is-loading="isSavingMemory"
        >
          {{
            ce(
              editingMemoryId
                ? 'CAPTAIN_EXPERIENCE.SCRIPT.UPDATE_MEMORY'
                : 'CAPTAIN_EXPERIENCE.AUTO.ADICIONAR_LEMBRANCA'
            )
          }}
        </Button>
        <Button
          v-if="editingMemoryId"
          type="button"
          color="slate"
          variant="faded"
          @click="emit('cancelMemoryEdit')"
        >
          {{ ce('CAPTAIN_EXPERIENCE.AUTO.CANCELAR') }}
        </Button>
      </form>

      <div class="mt-5 flex flex-col gap-2">
        <CardLayout v-for="memory in memories" :key="memory.id">
          <div class="flex items-start justify-between gap-3">
            <div class="text-xs font-bold uppercase text-n-blue-11">
              {{ memoryTypeLabel(memory) }}
            </div>
            <div v-if="canManage" class="flex gap-1">
              <Button
                type="button"
                size="xs"
                color="slate"
                variant="ghost"
                icon="i-lucide-pencil"
                :aria-label="ce('CAPTAIN_EXPERIENCE.SCRIPT.EDIT_MEMORY')"
                @click="emit('editMemory', memory)"
              />
              <Button
                type="button"
                size="xs"
                color="ruby"
                variant="ghost"
                icon="i-lucide-trash-2"
                :aria-label="ce('CAPTAIN_EXPERIENCE.SCRIPT.DELETE_MEMORY')"
                @click="emit('deleteMemory', memory)"
              />
            </div>
          </div>
          <p class="text-sm leading-relaxed text-n-slate-11">
            {{ memory.content }}
          </p>
        </CardLayout>
        <p
          v-if="memoriesTotalCount === 0"
          class="rounded-xl border border-dashed border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-11"
        >
          {{ ce('CAPTAIN_EXPERIENCE.AUTO.NENHUMA_LEMBRANCA_ADICIONADA_AINDA') }}
        </p>
        <Button
          v-if="hasMoreMemories"
          type="button"
          color="slate"
          variant="faded"
          :is-loading="isLoadingMoreMemories"
          :label="ce('CAPTAIN_EXPERIENCE.SCRIPT.LOAD_MORE')"
          @click="emit('loadMoreMemories')"
        />
      </div>
    </AgentSection>

    <AgentSection
      v-else
      :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.ADVANCED_KNOWLEDGE_TITLE')"
    >
      <div
        class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-4"
      >
        <span
          class="i-lucide-lock-keyhole mt-0.5 size-4 shrink-0 text-n-slate-10"
          aria-hidden="true"
        />
        <p class="text-sm leading-relaxed text-n-slate-11">
          {{
            ce('CAPTAIN_EXPERIENCE.WORKSPACE.ADVANCED_KNOWLEDGE_UNAVAILABLE')
          }}
        </p>
      </div>
    </AgentSection>
  </section>
</template>

<style scoped>
.agent-input,
.agent-textarea {
  @apply w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2.5 text-sm leading-normal text-n-slate-12 outline-none transition placeholder:text-n-slate-10 hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20;
}

.agent-input {
  min-height: 2.5rem;
}

.agent-textarea {
  resize: vertical;
}
</style>
